<template>
  <v-bottom-sheet
    ref="sheet" v-model="open"
    :persistent="persistent"
    :content-class="`bottom-sheet-draggable ${moving ? 'is-moving' : ''}`"
    :scrollable="false"
    :opacity="0.46"
    v-bind="$attrs"
    scrim="#212121"
  >
    <template #activator="{ props }">
      <slot name="activator" :props="props" />
    </template>

    <v-card ref="content" :color="color" class="rounded-b-0" flat style="overflow: visible;">
      <div ref="handle">
        <slot name="handle">
          <div class="handle text-center py-2">
            <div class="handle-grabber" :class="[handleColor]" :style="{color: handleColor}" />
          </div>
        </slot>
      </div>
      <v-card-text class="pa-0 bottom-sheet-content">
        <slot />
      </v-card-text>

      <v-sheet  :color="color" class="overscroll" />
    </v-card>
  </v-bottom-sheet>
</template>

<script setup>
import { ref, watch, onBeforeUnmount, nextTick, defineProps, defineEmits } from 'vue'
import Hammer from 'hammerjs'

// NOTE allow free scroll? (dont reset on end)
// NOTE handle hardware back button

// Define props
const props = defineProps({
    modelValue: Boolean,
    persistent: Boolean,
    threshold: {
      type: Number,
      default: 0.75,
    },
    color: {
      type: String,
      default: 'neutral25',
    },
    handleOnly: {
      type: Boolean,
      default: true,
    },
    handleColor: {
      type: String,
      default: 'bg-neutral600',
    },
})

// Define emits
const emit = defineEmits(['update:modelValue'])

// Refs
const sheet = ref(null)
const content = ref(null)
const handle = ref(null)
const mc = ref(null)
const open = ref(props.modelValue)
const moving = ref(false)

const handleUnmountOrClose = () => {
  if (mc.value) {
    mc.value.destroy()
    mc.value = null
  }
}

// Setup the hammer instance for touch/drag handling
const setup = () => {
  if (mc.value) mc.value.destroy()

  mc.value = new Hammer(handle.value)
  mc.value.get('pan').set({ direction: Hammer.DIRECTION_VERTICAL })
  mc.value.on('pan', onPan)
}

// Watch for modelValue changes
watch(() => props.modelValue, async active => {
  open.value = active

  if (!active && mc.value) {
    handleUnmountOrClose()
    return
  }

  if (active && !mc.value) {
    await nextTick()
    setup()
        }
}, { immediate: true })

// Watch for open changes to emit update events
watch(open, val => {
  emit('update:modelValue', val)
})

// Pan event handler
const onPan = e => {
  const contentEl = content.value.$el
      const height = contentEl.offsetHeight

      switch (e.eventType) {
        case Hammer.INPUT_END:
      // moving.value = false
      if (!props.persistent) {
        if (e.deltaY / height >= props.threshold) {
          onClose(e)
            } else if (e.velocityY > 0.75 && e.deltaY / height > 0.05) {
          onClose(e)
            }
          }
          requestAnimationFrame(() => {
            contentEl.style.transform = ''
          })
          moving.value = false
          break
        case Hammer.INPUT_MOVE:
      moving.value = true
          requestAnimationFrame(() => {
            contentEl.style.transform = `translateY(${e.deltaY}px)`
          })
          break
        default:
      }
}

// Close handler
const onClose = async e => {
  // sheet.value.closeConditional(e)
  open.value = false

  await nextTick()
  if (open.value) return

  // const contentEl = sheet.value.contentEl
  const contentEl = content.value.$el
      const height = contentEl.offsetHeight
      contentEl.style.transform = `translateY(${height}px)`

      setTimeout(() => {
        contentEl.style.transform = ''
      }, 500)
}

// Cleanup on unmount
onBeforeUnmount(() => {
  handleUnmountOrClose()
})
</script>

<style lang="scss" scoped>
.handle {
  cursor: grab;

  .handle-grabber {
    display: inline-block;
    width: 56px;
    height: 8px;
    border-radius: 50px;
  }
}

.v-card .bottom-sheet-content {
  max-height: 75vh;
  overflow-y: auto;
}

.v-card .overscroll {
  position: absolute;
  z-index: -1;
  bottom: 0;
  width: 100%;
  height: 100vh;
  content: ' ';
  transform: translateY(calc(100% - 1px));
}

.bottom-sheet-draggable.is-moving {
  transition-duration: 0.1s !important;
}
</style>
